import { useEffect, useState } from "react";
import { LogOut, Plus, Pencil, Trash2, Package, ShoppingCart, Star, Bell, Euro, TicketPercent, ImagePlus, Truck } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { formatPrice, FAMILIES, GENDERS, STATUS_STEPS } from "@/lib/constants";
import Reveal from "@/components/Reveal";

const EMPTY_PERFUME = {
  name: "", brand: "", gender: "Femme", family: "Floral", description: "", image: "",
  p30: "", p50: "", p100: "", tete: "", coeur: "", fond: "", occasions: "Quotidien",
  in_stock: true, featured: false,
};

const Admin = () => {
  const [user, setUser] = useState(undefined);
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [tab, setTab] = useState("orders");
  const [orders, setOrders] = useState([]);
  const [perfumes, setPerfumes] = useState([]);
  const [stats, setStats] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_PERFUME);
  const [loginError, setLoginError] = useState("");
  const [cryptoAddr, setCryptoAddr] = useState("");
  const [shippingFee, setShippingFee] = useState("");
  const [freeFrom, setFreeFrom] = useState("");
  const [instagramUrl, setInstagramUrl] = useState("");
  const [tiktokUrl, setTiktokUrl] = useState("");
  const [promos, setPromos] = useState([]);
  const [promoForm, setPromoForm] = useState({ code: "", percent: "", max_uses: "" });
  const [uploading, setUploading] = useState(false);

  const loadData = () => {
    api.get("/admin/orders").then((r) => setOrders(r.data)).catch(() => {});
    api.get("/perfumes").then((r) => setPerfumes(r.data)).catch(() => {});
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/admin/promos").then((r) => setPromos(r.data)).catch(() => {});
    api.get("/admin/settings").then((r) => {
      setCryptoAddr(r.data.crypto_address || "");
      setShippingFee(r.data.shipping_fee ?? "");
      setFreeFrom(r.data.free_shipping_from ?? "");
      setInstagramUrl(r.data.instagram_url || "");
      setTiktokUrl(r.data.tiktok_url || "");
    }).catch(() => {});
  };

  useEffect(() => {
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => setUser(null));
  }, []);

  useEffect(() => {
    if (user) loadData();
  }, [user]);

  const login = async (e) => {
    e.preventDefault();
    setLoginError("");
    try {
      const res = await api.post("/auth/login", loginForm);
      setUser(res.data);
      toast.success("Bienvenue dans votre espace ANM Paris");
    } catch (err) {
      setLoginError(formatApiError(err));
    }
  };

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    setUser(null);
  };

  const saveSettings = async () => {
    try {
      await api.put("/admin/settings", {
        crypto_address: cryptoAddr,
        shipping_fee: parseFloat(shippingFee) || 0,
        free_shipping_from: parseFloat(freeFrom) || 0,
        instagram_url: instagramUrl,
        tiktok_url: tiktokUrl,
      });
      toast.success("Réglages enregistrés");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const createPromo = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/promos", {
        code: promoForm.code,
        percent: parseFloat(promoForm.percent),
        max_uses: promoForm.max_uses ? parseInt(promoForm.max_uses, 10) : null,
      });
      toast.success(`Code ${promoForm.code.toUpperCase()} créé`);
      setPromoForm({ code: "", percent: "", max_uses: "" });
      loadData();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const togglePromo = async (p) => {
    try {
      await api.patch(`/admin/promos/${p.id}`, { active: !p.active });
      setPromos((prev) => prev.map((x) => (x.id === p.id ? { ...x, active: !p.active } : x)));
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const deletePromo = async (id) => {
    try {
      await api.delete(`/admin/promos/${id}`);
      setPromos((prev) => prev.filter((x) => x.id !== id));
      toast.success("Code supprimé");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, image: res.data.url }));
      toast.success("Photo téléversée");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };

  const openEdit = (p) => {
    if (p === "new") {
      setForm(EMPTY_PERFUME);
      setEditing("new");
    } else {
      setForm({
        name: p.name, brand: p.brand, gender: p.gender, family: p.family,
        description: p.description || "", image: p.image || "",
        p30: p.prices["30"], p50: p.prices["50"], p100: p.prices["100"],
        tete: (p.notes?.tete || []).join(", "), coeur: (p.notes?.coeur || []).join(", "), fond: (p.notes?.fond || []).join(", "),
        occasions: (p.occasions || []).join(", "), in_stock: p.in_stock, featured: !!p.featured,
      });
      setEditing(p.id);
    }
  };

  const savePerfume = async (e) => {
    e.preventDefault();
    const split = (s) => s.split(",").map((x) => x.trim()).filter(Boolean);
    const payload = {
      name: form.name, brand: form.brand, gender: form.gender, family: form.family,
      description: form.description, image: form.image,
      prices: { "30": parseFloat(form.p30), "50": parseFloat(form.p50), "100": parseFloat(form.p100) },
      notes: { tete: split(form.tete), coeur: split(form.coeur), fond: split(form.fond) },
      occasions: split(form.occasions), in_stock: form.in_stock, featured: form.featured,
    };
    try {
      if (editing === "new") await api.post("/admin/perfumes", payload);
      else await api.put(`/admin/perfumes/${editing}`, payload);
      toast.success(editing === "new" ? "Parfum ajouté" : "Parfum mis à jour");
      setEditing(null);
      loadData();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const deletePerfume = async (id) => {
    if (!window.confirm("Supprimer définitivement ce parfum ?")) return;
    try {
      await api.delete(`/admin/perfumes/${id}`);
      toast.success("Parfum supprimé");
      loadData();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/orders/${id}`, { status });
      setOrders((prev) => prev.map((o) => (o.id === id ? { ...o, status } : o)));
      toast.success(
        status === "expediee" || status === "livree"
          ? "Statut mis à jour — le client a été prévenu par email"
          : "Statut mis à jour"
      );
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (user === undefined) return <div className="py-24 text-center text-sm text-[#5C5449]">Chargement…</div>;

  if (!user)
    return (
      <div className="mx-auto max-w-md px-4 py-20" data-testid="admin-login">
        <Reveal className="rounded-3xl border border-[#EADFCB] bg-white p-8">
          <p className="text-center font-accent text-2xl tracking-[0.25em] text-[#1A1714]">ANM PARIS</p>
          <p className="mt-1 text-center text-xs uppercase tracking-[0.2em] text-[#C59B27]">Espace propriétaire</p>
          <form onSubmit={login} className="mt-8 space-y-3">
            <input data-testid="admin-email-input" type="email" required value={loginForm.email} onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })} placeholder="Email" className="w-full rounded-xl border border-[#EADFCB] bg-[#FAF8F5] px-4 py-3 text-sm outline-none focus:border-[#C59B27]" />
            <input data-testid="admin-password-input" type="password" required value={loginForm.password} onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })} placeholder="Mot de passe" className="w-full rounded-xl border border-[#EADFCB] bg-[#FAF8F5] px-4 py-3 text-sm outline-none focus:border-[#C59B27]" />
            {loginError && <p data-testid="admin-login-error" className="text-sm font-medium text-red-600">{loginError}</p>}
            <button data-testid="admin-login-button" className="w-full rounded-full bg-[#1A1714] py-3.5 text-sm font-semibold text-white transition-all hover:bg-[#C59B27] active:scale-95">
              Se connecter
            </button>
          </form>
        </Reveal>
      </div>
    );

  return (
    <div className="mx-auto max-w-7xl px-4 py-12 sm:px-8" data-testid="admin-dashboard">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-[#C59B27]">Espace propriétaire</p>
          <h1 className="mt-1 font-serif text-3xl text-[#1A1714]">Tableau de bord</h1>
        </div>
        <button data-testid="admin-logout-button" onClick={logout} className="flex items-center gap-2 rounded-full border border-[#EADFCB] bg-white px-5 py-2.5 text-xs font-semibold text-[#5C5449] transition-all hover:border-red-300 hover:text-red-500">
          <LogOut size={14} /> Déconnexion
        </button>
      </div>

      {stats && (
        <div className="mt-8 grid grid-cols-2 gap-4 lg:grid-cols-5" data-testid="admin-stats">
          {[
            { icon: Package, label: "Parfums", value: stats.perfumes },
            { icon: ShoppingCart, label: "Commandes", value: stats.orders },
            { icon: Euro, label: "Revenus", value: formatPrice(stats.revenue) },
            { icon: Star, label: "Avis", value: stats.reviews },
            { icon: Bell, label: "Alertes stock", value: stats.notifications },
          ].map((s) => (
            <div key={s.label} className="rounded-2xl border border-[#EADFCB] bg-white p-5">
              <s.icon size={18} className="text-[#C59B27]" />
              <p className="mt-3 font-serif text-2xl font-bold text-[#1A1714]">{s.value}</p>
              <p className="text-xs text-[#5C5449]">{s.label}</p>
            </div>
          ))}
        </div>
      )}

      <div className="mt-4 rounded-2xl border border-[#EADFCB] bg-white p-5" data-testid="admin-settings">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#C59B27]">Réglages boutique</p>
        <div className="mt-4 grid gap-5 lg:grid-cols-2">
          <div>
            <p className="text-sm font-semibold text-[#1A1714]">Adresse de réception crypto</p>
            <p className="mt-0.5 text-xs text-[#5C5449]">Affichée aux clients qui paient en crypto (Binance, Kraken, Trust Wallet, Phantom…).</p>
            <input
              data-testid="crypto-address-input"
              value={cryptoAddr}
              onChange={(e) => setCryptoAddr(e.target.value)}
              placeholder="Ex : bc1q… / adresse Ethereum / Solana"
              className="mt-2 w-full rounded-xl border border-[#EADFCB] bg-[#FAF8F5] px-4 py-2.5 text-sm outline-none focus:border-[#C59B27]"
            />
          </div>
          <div>
            <p className="flex items-center gap-1.5 text-sm font-semibold text-[#1A1714]"><Truck size={15} className="text-[#C59B27]" /> Frais de livraison</p>
            <p className="mt-0.5 text-xs text-[#5C5449]">Livraison offerte à partir du montant choisi (0 € = jamais offerte).</p>
            <div className="mt-2 flex gap-2">
              <input
                data-testid="shipping-fee-input"
                type="number"
                min="0"
                step="0.5"
                value={shippingFee}
                onChange={(e) => setShippingFee(e.target.value)}
                placeholder="Frais (€)"
                className="w-full rounded-xl border border-[#EADFCB] bg-[#FAF8F5] px-4 py-2.5 text-sm outline-none focus:border-[#C59B27]"
              />
              <input
                data-testid="free-shipping-input"
                type="number"
                min="0"
                step="5"
                value={freeFrom}
                onChange={(e) => setFreeFrom(e.target.value)}
                placeholder="Offerte dès (€)"
                className="w-full rounded-xl border border-[#EADFCB] bg-[#FAF8F5] px-4 py-2.5 text-sm outline-none focus:border-[#C59B27]"
              />
            </div>
          </div>
          <div>
            <p className="text-sm font-semibold text-[#1A1714]">Réseaux sociaux (ajoutables à tout moment)</p>
            <p className="mt-0.5 text-xs text-[#5C5449]">Collez vos liens Instagram et TikTok : ils apparaîtront dans le pied de page et sur l'accueil.</p>
            <div className="mt-2 space-y-2">
              <input
                data-testid="instagram-url-input"
                value={instagramUrl}
                onChange={(e) => setInstagramUrl(e.target.value)}
                placeholder="Lien Instagram (https://instagram.com/…)"
                className="w-full rounded-xl border border-[#EADFCB] bg-[#FAF8F5] px-4 py-2.5 text-sm outline-none focus:border-[#C59B27]"
              />
              <input
                data-testid="tiktok-url-input"
                value={tiktokUrl}
                onChange={(e) => setTiktokUrl(e.target.value)}
                placeholder="Lien TikTok (https://tiktok.com/@…)"
                className="w-full rounded-xl border border-[#EADFCB] bg-[#FAF8F5] px-4 py-2.5 text-sm outline-none focus:border-[#C59B27]"
              />
            </div>
          </div>
        </div>
        <button data-testid="settings-save" onClick={saveSettings} className="mt-4 rounded-xl bg-[#C59B27] px-6 py-2.5 text-xs font-semibold text-white transition-all hover:bg-[#A8821B] active:scale-95">
          Enregistrer les réglages
        </button>
      </div>

      <div className="mt-8 flex flex-wrap gap-2">
        {[
          { k: "orders", label: `Commandes (${orders.length})` },
          { k: "perfumes", label: `Parfums (${perfumes.length})` },
          { k: "promos", label: `Codes promo (${promos.length})` },
        ].map((t) => (
          <button key={t.k} data-testid={`admin-tab-${t.k}`} onClick={() => setTab(t.k)}
            className={`rounded-full px-5 py-2.5 text-sm font-semibold transition-all active:scale-95 ${tab === t.k ? "bg-[#1A1714] text-white" : "border border-[#EADFCB] bg-white text-[#5C5449] hover:border-[#C59B27]"}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === "orders" && (
        <div className="mt-6 space-y-4" data-testid="admin-orders-list">
          {orders.length === 0 && <p className="rounded-2xl border border-dashed border-[#EADFCB] p-8 text-center text-sm text-[#5C5449]">Aucune commande pour le moment.</p>}
          {orders.map((o) => (
            <div key={o.id} className="rounded-2xl border border-[#EADFCB] bg-white p-5">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <p className="font-accent text-lg tracking-[0.12em] text-[#1A1714]">{o.code}</p>
                  <p className="text-xs text-[#5C5449]">{new Date(o.created_at).toLocaleString("fr-FR")}</p>
                  <p className="mt-2 text-sm font-semibold text-[#1A1714]">{o.customer.name} — {o.customer.phone}</p>
                  <p className="text-xs text-[#5C5449]">{o.customer.email} · {o.customer.address}, {o.customer.city}</p>
                  {o.customer.notes && <p className="mt-1 text-xs italic text-[#5C5449]">« {o.customer.notes} »</p>}
                </div>
                <div className="text-right">
                  <p className="font-serif text-xl font-bold text-[#1A1714]">{formatPrice(o.total)}</p>
                  <p className="text-xs text-[#5C5449]">{o.shipping ? `dont livraison ${formatPrice(o.shipping)}` : "livraison offerte"}</p>
                  {o.promo_code && <p className="text-xs font-semibold text-[#C59B27]">Code {o.promo_code} (−{formatPrice(o.discount)})</p>}
                  <p className="text-xs capitalize text-[#5C5449]">{o.payment_method.replace("_", " ")}</p>
                  <select
                    data-testid={`order-status-${o.code}`}
                    value={o.status}
                    onChange={(e) => updateStatus(o.id, e.target.value)}
                    className="mt-2 rounded-xl border border-[#EADFCB] bg-[#FAF8F5] px-3 py-2 text-xs font-semibold outline-none focus:border-[#C59B27]"
                  >
                    {STATUS_STEPS.map((s) => (
                      <option key={s.key} value={s.key}>{s.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="mt-3 space-y-1 border-t border-[#EADFCB] pt-3">
                {o.items.map((i) => (
                  <p key={`${i.perfume_id}-${i.size}`} className="text-xs text-[#5C5449]">
                    {i.qty} × {i.name} ({i.brand}) — {i.size} ml · {formatPrice(i.unit_price * i.qty)}
                  </p>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "perfumes" && (
        <div className="mt-6" data-testid="admin-perfumes-list">
          <button data-testid="admin-add-perfume" onClick={() => openEdit("new")} className="flex items-center gap-2 rounded-full bg-[#C59B27] px-6 py-3 text-sm font-semibold text-white transition-all hover:bg-[#A8821B] active:scale-95">
            <Plus size={16} /> Ajouter un parfum
          </button>
          <div className="mt-5 overflow-x-auto rounded-2xl border border-[#EADFCB] bg-white">
            <table className="w-full min-w-[760px]">
              <thead>
                <tr className="border-b border-[#EADFCB] text-left text-xs uppercase tracking-[0.15em] text-[#C59B27]">
                  <th className="p-4">Parfum</th>
                  <th className="p-4">30 ml</th>
                  <th className="p-4">50 ml</th>
                  <th className="p-4">100 ml</th>
                  <th className="p-4">Stock</th>
                  <th className="p-4">Actions</th>
                </tr>
              </thead>
              <tbody>
                {perfumes.map((p) => (
                  <tr key={p.id} className="border-b border-[#F7EEDB] last:border-0">
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        {p.image && <img src={p.image} alt="" className="h-11 w-11 rounded-lg border border-[#EADFCB] object-cover" />}
                        <div>
                          <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[#C59B27]">{p.brand}</p>
                          <p className="text-sm font-semibold text-[#1A1714]">{p.name}</p>
                        </div>
                      </div>
                    </td>
                    <td className="p-4 text-sm text-[#5C5449]">{formatPrice(p.prices["30"])}</td>
                    <td className="p-4 text-sm text-[#5C5449]">{formatPrice(p.prices["50"])}</td>
                    <td className="p-4 text-sm text-[#5C5449]">{formatPrice(p.prices["100"])}</td>
                    <td className="p-4">
                      <span className={`rounded-full px-3 py-1 text-xs font-semibold ${p.in_stock ? "bg-[#F7EEDB] text-[#C59B27]" : "bg-red-50 text-red-600"}`}>
                        {p.in_stock ? "En stock" : "Rupture"}
                      </span>
                    </td>
                    <td className="p-4">
                      <div className="flex gap-2">
                        <button data-testid={`edit-perfume-${p.id}`} onClick={() => openEdit(p)} className="flex h-9 w-9 items-center justify-center rounded-full border border-[#EADFCB] text-[#5C5449] transition-colors hover:border-[#C59B27] hover:text-[#C59B27]" aria-label="Modifier">
                          <Pencil size={15} />
                        </button>
                        <button data-testid={`delete-perfume-${p.id}`} onClick={() => deletePerfume(p.id)} className="flex h-9 w-9 items-center justify-center rounded-full border border-[#EADFCB] text-[#5C5449] transition-colors hover:border-red-300 hover:text-red-500" aria-label="Supprimer">
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "promos" && (
        <div className="mt-6" data-testid="admin-promos">
          <form onSubmit={createPromo} className="flex flex-wrap items-center gap-3 rounded-2xl border border-[#EADFCB] bg-white p-5">
            <TicketPercent size={20} className="text-[#C59B27]" />
            <input
              data-testid="promo-form-code"
              required
              value={promoForm.code}
              onChange={(e) => setPromoForm({ ...promoForm, code: e.target.value.toUpperCase() })}
              placeholder="Code (ex : ANM20)"
              className="min-w-[160px] flex-1 rounded-xl border border-[#EADFCB] bg-[#FAF8F5] px-4 py-2.5 text-sm uppercase outline-none focus:border-[#C59B27]"
            />
            <input
              data-testid="promo-form-percent"
              required
              type="number"
              min="1"
              max="90"
              value={promoForm.percent}
              onChange={(e) => setPromoForm({ ...promoForm, percent: e.target.value })}
              placeholder="Réduction %"
              className="w-32 rounded-xl border border-[#EADFCB] bg-[#FAF8F5] px-4 py-2.5 text-sm outline-none focus:border-[#C59B27]"
            />
            <input
              data-testid="promo-form-max-uses"
              type="number"
              min="1"
              value={promoForm.max_uses}
              onChange={(e) => setPromoForm({ ...promoForm, max_uses: e.target.value })}
              placeholder="Utilisations max (vide = illimité)"
              className="min-w-[200px] flex-1 rounded-xl border border-[#EADFCB] bg-[#FAF8F5] px-4 py-2.5 text-sm outline-none focus:border-[#C59B27]"
            />
            <button data-testid="promo-form-create" className="flex items-center gap-2 rounded-full bg-[#C59B27] px-6 py-2.5 text-xs font-semibold text-white transition-all hover:bg-[#A8821B] active:scale-95">
              <Plus size={14} /> Créer le code
            </button>
          </form>

          <div className="mt-4 space-y-3">
            {promos.length === 0 && (
              <p className="rounded-2xl border border-dashed border-[#EADFCB] p-8 text-center text-sm text-[#5C5449]">
                Aucun code promo. Créez-en un pour récompenser vos clientes (ex : ANM20 = −20 %).
              </p>
            )}
            {promos.map((p) => (
              <div key={p.id} data-testid={`promo-row-${p.code}`} className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-[#EADFCB] bg-white p-4">
                <div>
                  <p className="font-accent text-lg tracking-[0.15em] text-[#1A1714]">{p.code}</p>
                  <p className="text-xs text-[#5C5449]">
                    −{p.percent}% sur toute la commande · {p.uses || 0}{p.max_uses ? ` / ${p.max_uses}` : ""} utilisation{(p.uses || 0) > 1 ? "s" : ""}
                    {p.max_uses ? "" : " (illimitées)"}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    data-testid={`promo-toggle-${p.code}`}
                    onClick={() => togglePromo(p)}
                    className={`rounded-full px-4 py-1.5 text-xs font-semibold transition-all active:scale-95 ${p.active ? "bg-[#F7EEDB] text-[#C59B27]" : "bg-[#1A1714]/5 text-[#5C5449]"}`}
                  >
                    {p.active ? "Actif" : "Inactif"}
                  </button>
                  <button data-testid={`promo-delete-${p.code}`} onClick={() => deletePromo(p.id)} className="flex h-9 w-9 items-center justify-center rounded-full border border-[#EADFCB] text-[#5C5449] transition-colors hover:border-red-300 hover:text-red-500" aria-label="Supprimer">
                    <Trash2 size={15} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-[#1A1714]/50 p-4 backdrop-blur-sm" data-testid="perfume-edit-modal">
          <form onSubmit={savePerfume} className="my-8 w-full max-w-2xl rounded-3xl bg-white p-8">
            <p className="font-serif text-2xl font-semibold text-[#1A1714]">
              {editing === "new" ? "Ajouter un parfum" : "Modifier le parfum"}
            </p>
            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              <input data-testid="perfume-form-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Nom du parfum" className="rounded-xl border border-[#EADFCB] bg-[#FAF8F5] px-4 py-3 text-sm outline-none focus:border-[#C59B27]" />
              <input data-testid="perfume-form-brand" required value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} placeholder="Marque" className="rounded-xl border border-[#EADFCB] bg-[#FAF8F5] px-4 py-3 text-sm outline-none focus:border-[#C59B27]" />
              <select value={form.gender} onChange={(e) => setForm({ ...form, gender: e.target.value })} className="rounded-xl border border-[#EADFCB] bg-[#FAF8F5] px-4 py-3 text-sm outline-none focus:border-[#C59B27]">
                {GENDERS.map((g) => <option key={g}>{g}</option>)}
              </select>
              <select value={form.family} onChange={(e) => setForm({ ...form, family: e.target.value })} className="rounded-xl border border-[#EADFCB] bg-[#FAF8F5] px-4 py-3 text-sm outline-none focus:border-[#C59B27]">
                {FAMILIES.map((f) => <option key={f}>{f}</option>)}
              </select>
              {[["p30", "Prix 30 ml (€)"], ["p50", "Prix 50 ml (€)"], ["p100", "Prix 100 ml (€)"]].map(([k, ph]) => (
                <input key={k} data-testid={`perfume-form-${k}`} required type="number" step="0.01" min="0" value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} placeholder={ph} className="rounded-xl border border-[#EADFCB] bg-[#FAF8F5] px-4 py-3 text-sm outline-none focus:border-[#C59B27]" />
              ))}
              <div className="sm:col-span-2">
                <p className="text-xs font-semibold uppercase tracking-[0.15em] text-[#C59B27]">Photo du parfum</p>
                <div className="mt-2 flex items-center gap-3">
                  {form.image && <img src={form.image} alt="Aperçu" data-testid="perfume-image-preview" className="h-16 w-16 rounded-xl border border-[#EADFCB] object-cover" />}
                  <label className={`flex cursor-pointer items-center gap-2 rounded-full border border-[#C59B27] px-5 py-2.5 text-xs font-semibold text-[#C59B27] transition-all hover:bg-[#C59B27] hover:text-white active:scale-95 ${uploading ? "pointer-events-none opacity-50" : ""}`}>
                    <ImagePlus size={15} /> {uploading ? "Envoi en cours…" : "Téléverser une photo"}
                    <input data-testid="perfume-form-upload" type="file" accept="image/*" className="hidden" onChange={uploadImage} />
                  </label>
                </div>
                <input value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} placeholder="…ou collez une URL d'image" className="mt-2 w-full rounded-xl border border-[#EADFCB] bg-[#FAF8F5] px-4 py-3 text-sm outline-none focus:border-[#C59B27]" />
              </div>
              <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} placeholder="Description" className="rounded-xl border border-[#EADFCB] bg-[#FAF8F5] px-4 py-3 text-sm outline-none focus:border-[#C59B27] sm:col-span-2" />
              <input value={form.tete} onChange={(e) => setForm({ ...form, tete: e.target.value })} placeholder="Notes de tête (séparées par des virgules)" className="rounded-xl border border-[#EADFCB] bg-[#FAF8F5] px-4 py-3 text-sm outline-none focus:border-[#C59B27]" />
              <input value={form.coeur} onChange={(e) => setForm({ ...form, coeur: e.target.value })} placeholder="Notes de cœur" className="rounded-xl border border-[#EADFCB] bg-[#FAF8F5] px-4 py-3 text-sm outline-none focus:border-[#C59B27]" />
              <input value={form.fond} onChange={(e) => setForm({ ...form, fond: e.target.value })} placeholder="Notes de fond" className="rounded-xl border border-[#EADFCB] bg-[#FAF8F5] px-4 py-3 text-sm outline-none focus:border-[#C59B27]" />
              <input value={form.occasions} onChange={(e) => setForm({ ...form, occasions: e.target.value })} placeholder="Occasions (Quotidien, Soirée, Rendez-vous)" className="rounded-xl border border-[#EADFCB] bg-[#FAF8F5] px-4 py-3 text-sm outline-none focus:border-[#C59B27]" />
            </div>
            <div className="mt-4 flex gap-6">
              <label className="flex items-center gap-2 text-sm text-[#5C5449]">
                <input data-testid="perfume-form-stock" type="checkbox" checked={form.in_stock} onChange={(e) => setForm({ ...form, in_stock: e.target.checked })} className="h-4 w-4 accent-[#C59B27]" /> En stock
              </label>
              <label className="flex items-center gap-2 text-sm text-[#5C5449]">
                <input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} className="h-4 w-4 accent-[#C59B27]" /> Mis en avant (accueil)
              </label>
            </div>
            <div className="mt-6 flex gap-3">
              <button data-testid="perfume-form-save" className="flex-1 rounded-full bg-[#C59B27] py-3.5 text-sm font-semibold text-white transition-all hover:bg-[#A8821B] active:scale-95">
                Enregistrer
              </button>
              <button type="button" data-testid="perfume-form-cancel" onClick={() => setEditing(null)} className="rounded-full border border-[#EADFCB] px-8 py-3.5 text-sm font-semibold text-[#5C5449] transition-all hover:border-[#C59B27]">
                Annuler
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default Admin;
